/** Resources: changes with resourcechange events, gainOn rules, regen/decay, combat flags. */
import type { AbilityId, ResourceDef, ResourceGainRule, ResourceId, SimContext, Unit, UnitId } from '../contracts';
import { TICK_MS } from '../contracts';
import { removeInstance } from './auras';
import { rearmCheatDeath } from './cheatDeath'; // +mage-specs (S9)
import { clamp, unitOf } from './util';

export function resourceDefs(ctx: SimContext, u: Unit): readonly ResourceDef[] {
  return u.classId ? (ctx.content.class(u.classId)?.resources ?? []) : [];
}

/** Add (or drain) a resource and emit resourcechange. Returns the actual change. */
export function changeResource(ctx: SimContext, unitId: UnitId, resource: ResourceId, amount: number, sourceId: UnitId, abilityId: AbilityId): number {
  const u = unitOf(ctx, unitId);
  const pool = u?.resources[resource];
  if (!u?.alive || !pool || amount === 0) return 0;
  const before = pool.cur;
  pool.cur = clamp(before + amount, 0, pool.max);
  const delta = pool.cur - before;
  ctx.emit({
    type: 'resourcechange',
    sourceID: sourceId,
    targetID: unitId,
    abilityGameID: abilityId,
    resource,
    resourceChange: amount,
    waste: amount > 0 ? amount - delta : 0,
    resourceAmount: pool.cur,
    maxResourceAmount: pool.max,
  });
  return delta;
}

/** Fire ResourceDef.gainOn rules for an event on `unitId`. `damage` feeds perPctMaxHp rules. */
export function applyGainRules(ctx: SimContext, unitId: UnitId, on: ResourceGainRule['on'], abilityId: AbilityId, damage = 0): void {
  const u = unitOf(ctx, unitId);
  if (!u?.alive) return;
  for (const def of resourceDefs(ctx, u)) {
    for (const rule of def.gainOn ?? []) {
      if (rule.on !== on) continue;
      const amt = rule.perPctMaxHp ? (rule.amount * damage * 100) / Math.max(1, u.maxHp) : rule.amount;
      if (amt > 0) changeResource(ctx, unitId, def.id, amt, unitId, abilityId);
    }
  }
}

/**
 * Players are in combat while any engaged NPC has them on its threat table or targets them (NPC combat
 * state belongs to the AI). Leaving combat drops removeOutOfCombat auras.
 */
function updatePlayerCombat(ctx: SimContext, u: Unit): void {
  let engaged = false;
  for (const id of ctx.world.unitOrder) {
    const n = ctx.world.units[id];
    if (n?.alive && n.inCombat && (n.threat?.entries[u.id] || n.targetId === u.id)) {
      engaged = true;
      break;
    }
  }
  if (u.inCombat && !engaged) {
    for (const inst of [...u.auras]) {
      if (ctx.content.aura(inst.auraId)?.flags?.removeOutOfCombat) removeInstance(ctx, u, inst, 'cancelled');
    }
    rearmCheatDeath(u); // +mage-specs (S9): Ashrise is once per fight
  }
  // +basekits: out-of-combat toggles (Swiftfoot, Lurk) drop the moment a fight starts (combat is
  // also flagged directly by damage and threat, so this checks every tick, not just the transition).
  if (u.inCombat || engaged) {
    for (const inst of [...u.auras]) {
      if (ctx.content.aura(inst.auraId)?.flags?.removeInCombat) removeInstance(ctx, u, inst, 'cancelled');
    }
  }
  u.inCombat = engaged;
}

/** Per-tick regen/decay (no events: passive regen would flood the log). */
export function tickResources(ctx: SimContext): void {
  for (const id of ctx.world.unitOrder) {
    const u = ctx.world.units[id];
    if (!u?.alive || u.kind !== 'player') continue;
    updatePlayerCombat(ctx, u);
    for (const def of resourceDefs(ctx, u)) {
      const pool = u.resources[def.id];
      if (!pool) continue;
      if (!u.inCombat && def.oocSettle !== undefined) {
        // +basekits: drift toward the resting value, never past it.
        const step = (Math.abs(def.regenPerSecOoc ?? 0) * TICK_MS) / 1000;
        const goal = clamp(def.oocSettle, 0, pool.max);
        pool.cur = pool.cur < goal ? Math.min(goal, pool.cur + step) : Math.max(goal, pool.cur - step);
        continue;
      }
      const rate = !u.inCombat && def.regenPerSecOoc !== undefined ? def.regenPerSecOoc : (def.regenPerSec ?? 0);
      if (rate !== 0) pool.cur = clamp(pool.cur + (rate * TICK_MS) / 1000, 0, pool.max);
    }
  }
}
